/**
 * URL Filter Parameter Utilities
 * Strategy 1: Selective Parameter Inclusion - Only include significant, non-default filter values
 */

export interface FilterDefaults {
  [key: string]: any
}

// Most important, least granular filters for each search type
export const PRIORITY_FILTERS = {
  vehicle: [
    // Core pricing & business type
    'price_min',
    'price_max',
    'is_business',

    // Vehicle basics
    'vehicle__brand',
    'vehicle__car_type',
    'vehicle__build_type',
    'vehicle__fuel_type',

    // Key specs
    'vehicle__mileage_min',
    'vehicle__mileage_max',
    'vehicle__year__gte',
    'vehicle__registration__gte',
    'vehicle__beds_min',
    'vehicle__seats_min',

    // Important equipment/features
    'vehicle__extras__slug',
    'vehicle__transmission',
    'vehicle__all_wheel_drive',
    'has_valid_general_inspection',
    'vehicle__is_serviced',

    // Size/dimensions
    'vehicle__size_length',
    'vehicle__size_height',

    // Pricing type
    'is_negotiable',
  ],
  market: [
    // Core pricing & business type
    'price_min',
    'price_max',
    'is_business',
    'is_negotiable',

    // Market specifics
    'categories',
    'condition',
    'item_type',
    'offer_type',
    'shipping',
  ],
  dealer: [
    // Core pricing & business type (shared with other search types)
    'price_min',
    'price_max',
    'is_business',
    'is_negotiable',

    // Dealer specifics
    'services',
  ],
} as const

// Default values that should not appear in URLs
export const FILTER_DEFAULTS: FilterDefaults = {
  price_min: 0,
  price_max: 1000000,
  vehicle__mileage_min: 0,
  vehicle__mileage_max: 1000000,
  vehicle__beds_min: undefined,
  vehicle__seats_min: undefined,
  vehicle__year__gte: undefined,
  vehicle__registration__gte: undefined,
  is_business: undefined,
  is_negotiable: undefined,
  vehicle__all_wheel_drive: undefined,
  has_valid_general_inspection: undefined,
  vehicle__is_serviced: undefined,
  condition: 'not_specified',
  shipping: 'not_specified',
  // Add more defaults as needed
}

/**
 * Check if a filter value is significant (not default/empty)
 */
export function isSignificantFilterValue(key: string, value: any): boolean {
  // Null, undefined, empty string
  if (value === null || value === undefined || value === '') {
    return false
  }

  // Empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return false
  }

  // Check against defaults
  if (key in FILTER_DEFAULTS && value === FILTER_DEFAULTS[key]) {
    return false
  }

  return true
}

/**
 * Get priority filters that should be included in URL for a search type
 */
export function getPriorityFiltersForSearchType(searchType: string): string[] {
  const filters =
    PRIORITY_FILTERS[searchType as keyof typeof PRIORITY_FILTERS] || []
  return [...filters] // Convert readonly array to mutable array
}

/**
 * Serialize a filter value for URL parameter
 */
export function serializeFilterValue(key: string, value: any): string {
  if (Array.isArray(value)) {
    return value.join(',')
  }

  if (typeof value === 'boolean') {
    return value.toString()
  }

  return String(value)
}

/**
 * Deserialize URL parameter to filter value
 */
export function deserializeFilterValue(key: string, urlValue: string): any {
  // Handle arrays (brand, car_type, categories, services, extras, etc.)
  if (
    key.includes('brand') ||
    key.includes('car_type') ||
    key.includes('build_type') ||
    key.includes('fuel_type') ||
    key.includes('transmission') ||
    key.includes('size_length') ||
    key.includes('size_height') ||
    key.includes('extras__slug') ||
    key === 'categories' ||
    key === 'services'
  ) {
    const arrayValues = urlValue.split(',').filter((v) => v.length > 0)
    // Deduplicate array values to prevent duplicates from URL parsing or merging
    return [...new Set(arrayValues)]
  }

  // Handle numbers (price, mileage, year, beds, seats, registration)
  if (
    key.includes('price') ||
    key.includes('mileage') ||
    key.includes('year') ||
    key.includes('beds') ||
    key.includes('seats') ||
    key.includes('registration')
  ) {
    const num = Number(urlValue)
    return !isNaN(num) ? num : undefined
  }

  // Handle booleans
  if (
    key === 'is_business' ||
    key === 'is_negotiable' ||
    key === 'all_wheel_drive' ||
    key === 'has_valid_general_inspection' ||
    key.includes('is_serviced')
  ) {
    return urlValue === 'true'
  }

  // Default: string (condition, item_type, offer_type, shipping)
  return urlValue
}

/**
 * Extract significant filter parameters from filterData for URL
 * Only includes priority filters with significant values
 */
export function extractSignificantUrlParams(
  filterData: any,
  searchType: string
): Record<string, string> {
  const priorityFilters = getPriorityFiltersForSearchType(searchType)
  const urlParams: Record<string, string> = {}

  priorityFilters.forEach((key) => {
    // Get value from flat structure
    const value = getNestedValue(filterData, key)
    const isSignificant = isSignificantFilterValue(key, value)

    if (isSignificant) {
      urlParams[key] = serializeFilterValue(key, value)
    }
  })

  return urlParams
}

/**
 * Helper function to set flat key-value pairs
 * The search form expects flat structure like filterData.vehicle__registration__gte
 * not nested like filterData.vehicle.registration.gte
 */
function setNestedValue(obj: any, flatKey: string, value: any) {
  // Use flat key directly - the search form expects this structure
  obj[flatKey] = value
}

/**
 * Helper function to get nested values using flat keys
 */
/**
 * Helper function to get values using flat keys
 * The search form uses flat structure like filterData.vehicle__registration__gte
 */
function getNestedValue(obj: any, flatKey: string): any {
  // Use flat key directly since that's how the data is stored
  return obj[flatKey]
}

/**
 * Get all priority filters from all search types except the current one
 */
export function getAllPriorityFiltersExcept(
  currentSearchType: string
): string[] {
  const allFilters: string[] = []

  Object.entries(PRIORITY_FILTERS).forEach(([searchType, filters]) => {
    if (searchType !== currentSearchType) {
      allFilters.push(...filters)
    }
  })

  // Remove duplicates (some filters might be shared across search types)
  return [...new Set(allFilters)]
}

/**
 * Deduplicate all array values in filter data
 *
 * Design Decision: This function deduplicates ALL array fields generically rather than
 * maintaining a hardcoded list of specific fields. This is because:
 *
 * 1. Duplicate values in filter arrays never serve a legitimate purpose - they represent
 *    user error, system bugs, or data corruption
 * 2. Generic approach provides future-proofing for new array fields without code changes
 * 3. Better maintainability - no need to update lists when adding new filter types
 * 4. Cleaner, simpler code with automatic deduplication
 *
 * This provides future-proofing and better maintainability by automatically
 * deduplicating ANY array field without needing to maintain a hardcoded list
 */
export function deduplicateFilterArrays(
  filterData: Record<string, any>
): Record<string, any> {
  const result = { ...filterData }

  Object.keys(result).forEach((field) => {
    if (Array.isArray(result[field])) {
      result[field] = [...new Set(result[field])]
    }
  })

  return result
}

/**
 * Get parameters that should be cleaned up when switching search types
 */
export function getParametersToCleanupOnSearchTypeChange(
  currentQuery: Record<string, any>,
  newSearchType: string,
  oldSearchType?: string
): string[] {
  if (!oldSearchType || oldSearchType === newSearchType) {
    return []
  }

  const newSearchTypePriorityFilters =
    getPriorityFiltersForSearchType(newSearchType)
  const parametersFromOtherSearchTypes =
    getAllPriorityFiltersExcept(newSearchType)
  const parametersToCleanup: string[] = []

  Object.keys(currentQuery).forEach((key) => {
    // Skip basic parameters that should be preserved across search types
    if (['s', 'l', 'r', 't', 'cat'].includes(key)) {
      return
    }

    // If parameter belongs to other search types and not to current search type, clean it up
    if (
      parametersFromOtherSearchTypes.includes(key) &&
      !newSearchTypePriorityFilters.includes(key)
    ) {
      parametersToCleanup.push(key)
    }
  })

  return parametersToCleanup
}

/**
 * Parse URL query parameters into filter data
 */
export function parseUrlParamsToFilterData(
  queryParams: Record<string, any>,
  searchType: string
): Record<string, any> {
  const priorityFilters = getPriorityFiltersForSearchType(searchType)
  const filterData: Record<string, any> = {}

  Object.entries(queryParams).forEach(([key, value]) => {
    const isInPriorityFilters = priorityFilters.includes(key)
    const isValidValue = value !== undefined && value !== ''

    if (isInPriorityFilters && isValidValue) {
      const stringValue = Array.isArray(value) ? value[0] : String(value)
      const deserializedValue = deserializeFilterValue(key, stringValue)

      // Store using flat key structure that the search form expects
      setNestedValue(filterData, key, deserializedValue)
    }
  })

  return filterData
}
